import {
  Home, Calendar, CalendarDays, CalendarRange, CalendarClock, Tag, Folder,
  MapPin, Scissors, Sparkles, Users,
} from "lucide-react";

// Fælles undermenu-skabelon (Forecast-templaten): vandrette understreg-faner med
// base-linje + vandret scroll på smalle skærme. ÉT sted at rette stilen for ALLE
// undermenuer (Hub, Performance/Score periode-faner, Forecast-filter,
// Elever-by-menu, Undervisning-retning, Karakter-sub-faner).
//
// Ikoner er OPT-IN: kun submenuer der sætter `icons` får lucide-ikoner — pt. KUN
// Forsiden/Hub-undermenuen. Alle øvrige submenuer er rene tekst-faner. Ikon-
// mappingen er central her (ICON_BY_KEY / BY_KEYS) — tilføj nye keys her, ikke
// på kald-stederne.
//
// Props:
//  - tabs: [{ key, label }]  (eller [{ id, label }] — begge accepteres)
//      Valgfrit pr. fane: { done, total } → tæller-delen af label (efter " · ")
//      bliver GRØN når done >= total (> 0). Bruges af Karakter-fanens
//      modul/gruppe-sub-faner: "Grundmodul · 4/4" lyser grønt når alt er bestået.
//  - value: aktiv key
//  - onChange(key)
//  - icons: vis lucide-ikon pr. fane (default false → ingen ikoner)
//  - className: ekstra klasser på container (fx responsive visibility)

// key (lowercase) → lucide-ikon. Byer rammes via fallback (de er ikke i mappet).
const ICON_BY_KEY = {
  // Hub-undermenu
  overview: Home,
  kalender: Calendar,
  tilbud: Tag,
  filer: Folder,
  // Periode-faner (Performance/Score)
  day: Calendar,
  week: CalendarDays,
  month: CalendarRange,
  last_month: CalendarClock,
  prev_month: CalendarClock,
  // Forecast-type-filter
  all: Users,
  kos: Sparkles,        // kosmetolog
  fri: Scissors,        // frisør
  kosmetolog: Sparkles,
  frisor: Scissors,
  // Elever-by-menu
  alle: Users,
};

// Kendte by-keys (display-navne) → kort-ikon. Alt andet ukendt = MapPin-fallback.
const BY_KEYS = new Set(["Koebenhavn", "København", "Aarhus", "Odense", "Aalborg", "Kolding", "Administration", "TNBC"]);

function ikonFor(key) {
  if (key == null) return null;
  const k = String(key);
  if (ICON_BY_KEY[k.toLowerCase()]) return ICON_BY_KEY[k.toLowerCase()];
  if (BY_KEYS.has(k)) return MapPin;  // by-faner
  return null;
}

export default function SubTabs({ tabs = [], value, onChange, className = "", icons = false }) {
  return (
    <div
      className={`flex flex-nowrap gap-1 sm:gap-1.5 border-b border-border overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${className}`}
    >
      {tabs.map((t) => {
        const key = t.key ?? t.id;
        const aktiv = value === key;
        // Ikoner er OPT-IN (icons=true) og bruges kun på Forsiden/Hub-undermenuen.
        // Alle andre submenuer er rene tekst-faner.
        const Ikon = icons ? ikonFor(key) : null;
        // Grøn tæller når gruppen er fuldt udfyldt. Vi splitter på det sidste
        // " · " så kun selve tælleren (fx "4/4") farves — resten af label står
        // i fane-farven. Kun aktivt når tab har {done,total} og done>=total>0.
        const komplet =
          typeof t.done === "number" && typeof t.total === "number" &&
          t.total > 0 && t.done >= t.total;
        let labelHoved = t.label;
        let labelTaeller = null;
        if (komplet) {
          const i = t.label.lastIndexOf(" · ");
          if (i !== -1) {
            labelHoved = t.label.slice(0, i + 3); // inkl. " · "
            labelTaeller = t.label.slice(i + 3);
          }
        }
        return (
          <button
            key={key}
            onClick={() => onChange(key)}
            className={`flex items-center gap-1.5 px-2 sm:px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap shrink-0 ${
              aktiv
                ? "border-[#FF2A6D] text-[#FF2A6D]"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {Ikon && <Ikon className="w-4 h-4 shrink-0" />}
            {labelHoved}
            {labelTaeller && (
              <span className="text-green-600 dark:text-green-400">{labelTaeller}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
